import memoize from 'moize'

interface CachedRequestParams<Args, Result> {
  request: (args: Args) => Promise<Result>
  maxAge: number
}

interface CachedRequestWhenParams<Args, Result> extends CachedRequestParams<Args, Result> {
  shouldCache: (args: Args) => boolean
}

const MAX_CACHED_ARGUMENTS = 50

export const cachedRequest = <Args, Result>({
  request,
  maxAge,
}: CachedRequestParams<Args, Result>) =>
  memoize(request, { isPromise: true, isDeepEqual: true, maxAge, maxSize: MAX_CACHED_ARGUMENTS })

export const shareInFlight = <Args, Result>(request: (args: Args) => Promise<Result>) => {
  const inFlight = new Map<string, Promise<Result>>()

  const shared = (args: Args) => {
    const key = JSON.stringify(args)
    const pending = inFlight.get(key)

    if (pending) return pending

    const promise: Promise<Result> = request(args).finally(() => {
      if (inFlight.get(key) === promise) inFlight.delete(key)
    })
    inFlight.set(key, promise)

    return promise
  }

  return Object.assign(shared, { clear: () => inFlight.clear() })
}

export const cachedRequestWhen = <Args, Result>({
  request,
  maxAge,
  shouldCache,
}: CachedRequestWhenParams<Args, Result>) => {
  const cached = cachedRequest({ request, maxAge })
  const shared = shareInFlight(request)

  return Object.assign((args: Args) => (shouldCache(args) ? cached(args) : shared(args)), {
    clear: () => {
      cached.clear()
      shared.clear()
    },
  })
}
